import React, { useState, useRef, useEffect, useMemo } from "react";
import * as DateUtils from "../utils/dateUtils.js";
import { getAllProjects } from "../services/azureProjectsService";
import { getHrSettings } from "../services/azureHrService";

const MobileCalendar = ({
  currentWeek,
  setCurrentWeek,
  selectedCells,
  setSelectedCells,
  onCreateEntry,
  onDeleteEntry,
  onEditEntry,
  timeEntries,
  contracts,
  themeValues,
  loading = false,
}) => {
  const [currentDay, setCurrentDay] = useState(new Date());
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState(null);
  const [touchStartY, setTouchStartY] = useState(null);
  const [projects, setProjects] = useState([]);
  const [hrSettings, setHrSettings] = useState(null);
  const scrollContainerRef = useRef(null);

  // Load projects for color lookup
  useEffect(() => {
    const loadProjects = async () => {
      try {
        const projectsData = await getAllProjects();
        setProjects(projectsData);
      } catch (error) {
        console.error("Error loading projects for colors:", error);
      }
    };
    loadProjects();
  }, []);

  // HR-Settings (Feiertage/Betriebsferien)
  useEffect(() => {
    const year = String(currentWeek.getFullYear());
    getHrSettings(year)
      .then(setHrSettings)
      .catch(() => setHrSettings(null));
  }, [currentWeek]);

  const feiertageSet = useMemo(
    () =>
      new Set(
        (hrSettings?.feiertage || []).map((f) =>
          typeof f === "string" ? f : f.date
        )
      ),
    [hrSettings]
  );
  const betriebsferienSet = useMemo(
    () => new Set(hrSettings?.betriebsferien || []),
    [hrSettings]
  );

  const getDayKind = (day) => {
    const dateStr = DateUtils.formatDate(day);
    const dow = day.getDay();
    if (dow === 0 || dow === 6) return "weekend";
    if (feiertageSet.has(dateStr)) return "feiertag";
    if (betriebsferienSet.has(dateStr)) return "betriebsferien";
    return null;
  };

  const getFeiertagName = (day) => {
    const dateStr = DateUtils.formatDate(day);
    const match = (hrSettings?.feiertage || []).find(
      (f) => (typeof f === "string" ? f : f.date) === dateStr
    );
    return match && typeof match === "object" ? match.name : null;
  };

  const getFullDayEntriesForDay = (day) => {
    const cellDate = DateUtils.formatDate(day);
    return timeEntries.filter(
      (entry) => entry.isFullDay && entry.date === cellDate
    );
  };

  const hours = Array.from({ length: 19 }, (_, i) => i + 5); // 5:00 bis 23:30
  const weekDays = DateUtils.getWeekDays(currentWeek);

  // Set current day to match current week
  useEffect(() => {
    const todayInWeek = weekDays.find(
      (day) => DateUtils.formatDate(day) === DateUtils.formatDate(new Date())
    );
    if (todayInWeek) {
      setCurrentDay(todayInWeek);
    } else {
      setCurrentDay(weekDays[0]); // Fallback to Monday
    }
  }, [currentWeek]);

  // Navigation zwischen Tagen
  const navigateDay = (direction) => {
    const newDay = new Date(currentDay);
    newDay.setDate(newDay.getDate() + direction);

    // Check if new day is in current week, otherwise change week
    const newDayInCurrentWeek = weekDays.find(
      (day) => DateUtils.formatDate(day) === DateUtils.formatDate(newDay)
    );

    if (newDayInCurrentWeek) {
      setCurrentDay(newDay);
    } else {
      // Change week and set new day
      const newWeek = new Date(currentWeek);
      newWeek.setDate(newWeek.getDate() + direction * 7);
      setCurrentWeek(newWeek);
      setCurrentDay(newDay);
    }

    setSelectedCells([]);
  };

  // Cell Selection Logic
  const getCellKey = (hour, isHalf) => {
    return `${DateUtils.formatDate(currentDay)}-${hour}-${isHalf}`;
  };

  const isCellSelected = (hour, isHalf) => {
    const key = getCellKey(hour, isHalf);
    return selectedCells.some((cell) => cell.key === key);
  };

  const getTimeEntryForCell = (hour, isHalf) => {
    const cellTime = DateUtils.formatTime(hour, isHalf);
    const cellDate = DateUtils.formatDate(currentDay);

    return timeEntries.find((entry) => {
      if (entry.date !== cellDate) return false;
      // Ganztag-Eintraege belegen keine Zellen — als Pill im Header sichtbar
      if (entry.isFullDay) return false;
      const entryStart = entry.startTime;
      const entryEnd = entry.endTime;
      return cellTime >= entryStart && cellTime < entryEnd;
    });
  };

  const getCellClassName = (hour, isHalf) => {
    const classes = ["mobile-calendar-cell"];
    const timeEntry = getTimeEntryForCell(hour, isHalf);

    if (timeEntry) {
      classes.push("occupied");
    } else if (isCellSelected(hour, isHalf)) {
      classes.push("selected");
    } else {
      classes.push("available");
    }

    // Non-working: Hintergrundhinweis, bleibt interaktiv
    const dayKind = getDayKind(currentDay);
    const hasAbsence = getFullDayEntriesForDay(currentDay).length > 0;
    if (dayKind || hasAbsence) {
      classes.push("non-working");
      if (dayKind) classes.push(`nw-${dayKind}`);
      if (hasAbsence) classes.push("nw-absence");
    }

    return classes.join(" ");
  };

  // Touch/Mouse Selection Logic
  const handleSelectionStart = (hour, isHalf, e) => {
    e.preventDefault();
    const timeEntry = getTimeEntryForCell(hour, isHalf);

    if (timeEntry) {
      if (onEditEntry) onEditEntry(timeEntry);
      else if (onDeleteEntry) onDeleteEntry(timeEntry);
      return;
    }

    setIsSelecting(true);
    setSelectionStart({ hour, isHalf });

    const cellData = {
      key: getCellKey(hour, isHalf),
      day: currentDay,
      hour,
      isHalf,
      time: DateUtils.formatTime(hour, isHalf),
      date: DateUtils.formatDate(currentDay),
    };

    setSelectedCells([cellData]);
  };

  const handleSelectionMove = (hour, isHalf) => {
    if (!isSelecting || !selectionStart) return;

    const startHourIndex =
      (selectionStart.hour - 5) * 2 + (selectionStart.isHalf ? 1 : 0);
    const endHourIndex = (hour - 5) * 2 + (isHalf ? 1 : 0);

    const minIndex = Math.min(startHourIndex, endHourIndex);
    const maxIndex = Math.max(startHourIndex, endHourIndex);

    const newSelection = [];

    for (let i = minIndex; i <= maxIndex; i++) {
      const cellHour = Math.floor(i / 2) + 5;
      const cellIsHalf = i % 2 === 1;

      if (cellHour >= 5 && cellHour <= 23) {
        const timeEntry = getTimeEntryForCell(cellHour, cellIsHalf);
        if (!timeEntry) {
          newSelection.push({
            key: getCellKey(cellHour, cellIsHalf),
            day: currentDay,
            hour: cellHour,
            isHalf: cellIsHalf,
            time: DateUtils.formatTime(cellHour, cellIsHalf),
            date: DateUtils.formatDate(currentDay),
          });
        }
      }
    }

    setSelectedCells(newSelection);
  };

  const handleSelectionEnd = () => {
    setIsSelecting(false);
    setSelectionStart(null);
    setTouchStartY(null);
  };

  // Touch Events
  const handleTouchStart = (hour, isHalf, e) => {
    // Prevent default um scrolling zu verhindern
    e.preventDefault();
    const touch = e.touches[0];
    setTouchStartY(touch.clientY);
    handleSelectionStart(hour, isHalf, e);
  };

  const handleTouchMove = (e) => {
    if (!isSelecting || touchStartY === null) return;

    // Prevent default um scrolling zu verhindern
    e.preventDefault();

    const touch = e.touches[0];
    const element = document.elementFromPoint(touch.clientX, touch.clientY);

    if (
      element &&
      element.dataset.hour &&
      element.dataset.isHalf !== undefined
    ) {
      const hour = parseInt(element.dataset.hour);
      const isHalf = element.dataset.isHalf === "true";
      handleSelectionMove(hour, isHalf);
    }
  };

  const handleTouchEnd = (e) => {
    e.preventDefault();
    handleSelectionEnd();
  };

  // Get contract info for styling - exakt wie Desktop Calendar
  const getContractInfo = (contractUuid) => {
    const contract = contracts.find((c) => c.uuid === contractUuid);
    return (
      contract || {
        reference: "Unbekannt",
        displayTitle: "Unbekannter Auftrag",
        fullReference: "Unbekannt",
        customer: "Unbekannt",
      }
    );
  };

  const getContractColor = (contractUuid) => {
    if (!contractUuid) {
      return { bg: "#6b7280", text: "#ffffff" };
    }

    // Try to find the contract and get project color
    const contract = contracts.find((c) => c.uuid === contractUuid);
    if (contract && contract.reference) {
      // Extract project code from reference (first 3 characters)
      const projectCode = contract.reference.substring(0, 3).toUpperCase();
      const project = projects.find((p) => p.code?.toUpperCase() === projectCode);
      if (project && project.color) {
        return { bg: project.color, text: "#ffffff" };
      }
    }

    // Fallback to hash-based color if no project color found
    const fallbackColors = [
      { bg: "#3b82f6", text: "#ffffff" },
      { bg: "#ef4444", text: "#ffffff" },
      { bg: "#10b981", text: "#ffffff" },
      { bg: "#f59e0b", text: "#ffffff" },
      { bg: "#8b5cf6", text: "#ffffff" },
      { bg: "#06b6d4", text: "#ffffff" },
    ];

    const hash = contractUuid.split("").reduce((a, b) => {
      a = (a << 5) - a + b.charCodeAt(0);
      return a & a;
    }, 0);

    return fallbackColors[Math.abs(hash) % fallbackColors.length];
  };

  const isFirstCellOfEntry = (hour, isHalf, entry) => {
    const cellTime = DateUtils.formatTime(hour, isHalf);
    return cellTime === entry.startTime;
  };

  // Get week number
  const getWeekNumber = (date) => {
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear = (date - firstDayOfYear) / 86400000;
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
  };

  // Get day name
  const getDayName = (date) => {
    const days = [
      "Sonntag",
      "Montag",
      "Dienstag",
      "Mittwoch",
      "Donnerstag",
      "Freitag",
      "Samstag",
    ];
    return days[date.getDay()];
  };

  const formatCurrentDay = (date) => {
    const day = getDayName(date);
    const dateStr = date.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
    });
    return `${day}, ${dateStr}`;
  };

  return (
    <div className="mobile-calendar-container">
      {/* Mobile Header */}
      <div className="mobile-calendar-header">
        <button
          className="mobile-nav-button prev"
          onClick={() => navigateDay(-1)}
        >
          ←
        </button>

        <div className="mobile-day-display">
          <div className="mobile-day-name">{formatCurrentDay(currentDay)}</div>
          <div className="mobile-week-info">
            KW {getWeekNumber(currentWeek)}
          </div>
          {(() => {
            const dayKind = getDayKind(currentDay);
            const feiertagName = getFeiertagName(currentDay);
            const fullDayEntries = getFullDayEntriesForDay(currentDay);
            if (!dayKind && fullDayEntries.length === 0) return null;
            return (
              <div className="day-header-badges">
                {dayKind === "feiertag" && (
                  <span className="absence-pill pill-feiertag">
                    {feiertagName || "Feiertag"}
                  </span>
                )}
                {dayKind === "betriebsferien" && (
                  <span className="absence-pill pill-betriebsferien">
                    Betriebsferien
                  </span>
                )}
                {fullDayEntries.map((entry) => (
                  <button
                    key={`m-absence-${entry.uuid || entry.date}-${entry.typeName}`}
                    type="button"
                    className="absence-pill pill-absence"
                    title={`${entry.typeName || "Abwesenheit"} — Klicken zum Bearbeiten`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (onEditEntry) onEditEntry(entry);
                      else if (onDeleteEntry) onDeleteEntry(entry);
                    }}
                  >
                    {entry.typeName || "Abwesenheit"}
                  </button>
                ))}
              </div>
            );
          })()}
        </div>

        <button
          className="mobile-nav-button next"
          onClick={() => navigateDay(1)}
        >
          →
        </button>
      </div>

      {/* Day Selector Dots */}
      <div className="mobile-day-selector">
        {weekDays.map((day, index) => (
          <button
            key={index}
            className={`day-dot ${
              DateUtils.formatDate(day) === DateUtils.formatDate(currentDay)
                ? "active"
                : ""
            }`}
            onClick={() => setCurrentDay(day)}
          >
            <span className="day-dot-label">
              {["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"][index]}
            </span>
          </button>
        ))}
      </div>

      {/* Mobile Calendar Grid */}
      <div
        className={`mobile-calendar-scroll ${loading ? "loading" : ""}`}
        ref={scrollContainerRef}
        onTouchEnd={handleTouchEnd}
        onMouseUp={handleSelectionEnd}
        onMouseLeave={handleSelectionEnd}
        style={{ touchAction: "pan-y" }} // Nur vertikales Scrollen erlauben
      >
        <div className="mobile-calendar-grid" onTouchMove={handleTouchMove}>
          {hours.map((hour) => (
            <div key={hour} className="mobile-hour-block">
              <div className="mobile-time-label">
                {DateUtils.formatTime(hour, false)}
              </div>
              <div className="mobile-time-cells">
                {/* Full Hour Cell */}
                <div
                  className={getCellClassName(hour, false)}
                  data-hour={hour}
                  data-is-half="false"
                  onMouseDown={(e) => handleSelectionStart(hour, false, e)}
                  onMouseEnter={() => handleSelectionMove(hour, false)}
                  onTouchStart={(e) => handleTouchStart(hour, false, e)}
                  style={(() => {
                    const entry = getTimeEntryForCell(hour, false);
                    if (entry) {
                      const colors = getContractColor(entry.contractUuid);
                      return {
                        backgroundColor: colors.bg,
                        color: colors.text,
                        borderColor: colors.bg,
                      };
                    }
                    return {};
                  })()}
                >
                  {(() => {
                    const entry = getTimeEntryForCell(hour, false);
                    if (entry && isFirstCellOfEntry(hour, false, entry)) {
                      const contract = getContractInfo(entry.contractUuid);
                      console.log("contract", contract);
                      return (
                        <div className="mobile-entry-info">
                          <div className="mobile-entry-ref">
                            {contract.reference === "Unbekannt" || entry === undefined
                              ? entry.typeName
                              : contract.reference}
                          </div>
                          <div className="mobile-entry-time">
                            {entry.startTime} - {entry.endTime}
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>

                {/* Half Hour Cell */}
                <div
                  className={getCellClassName(hour, true)}
                  data-hour={hour}
                  data-is-half="true"
                  onMouseDown={(e) => handleSelectionStart(hour, true, e)}
                  onMouseEnter={() => handleSelectionMove(hour, true)}
                  onTouchStart={(e) => handleTouchStart(hour, true, e)}
                  style={(() => {
                    const entry = getTimeEntryForCell(hour, true);
                    if (entry) {
                      const colors = getContractColor(entry.contractUuid);
                      return {
                        backgroundColor: colors.bg,
                        color: colors.text,
                        borderColor: colors.bg,
                      };
                    }
                    return {};
                  })()}
                >
                  {(() => {
                    const entry = getTimeEntryForCell(hour, true);
                    console.log("entry:", entry);
                    if (entry && isFirstCellOfEntry(hour, true, entry)) {
                      const contract = getContractInfo(entry.contractUuid);
                      console.log("contract: ", contract);

                      return (
                        <div className="mobile-entry-info">
                          <div className="mobile-entry-ref">
                            {contract.reference === "Unbekannt" || entry === undefined
                              ? entry.typeName
                              : contract.reference}
                          </div>
                          <div className="mobile-entry-time">
                            {entry.startTime} - {entry.endTime}
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Mobile Action Button */}
      {selectedCells.length > 0 && (
        <div className="mobile-action-bar">
          <button className="mobile-create-button" onClick={onCreateEntry}>
            Eintrag erstellen ({selectedCells.length} Blöcke)
          </button>
          <button
            className="mobile-clear-button"
            onClick={() => setSelectedCells([])}
          >
            Abbrechen
          </button>
        </div>
      )}
    </div>
  );
};

export default MobileCalendar;
